import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import { useCart } from "@/lib/cart";
import { api } from "@/lib/api";
import { formatPrice, resolveImage } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Download } from "lucide-react";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onPay = async () => {
    if (!email || !/.+@.+\..+/.test(email)) { toast.error("Enter a valid email"); return; }
    if (items.length === 0) { toast.error("Your cart is empty"); return; }

    // Open a new browser tab SYNCHRONOUSLY inside the click handler so the
    // popup blocker treats it as user-initiated. We'll point it at the Stripe
    // checkout URL once the API returns. This is the only pattern that
    // reliably escapes an embedded iframe context (e.g. the Emergent preview
    // iframe) — a same-frame navigation would try to load checkout.stripe.com
    // inside the sandboxed iframe and get stuck on Stripe's skeleton loader
    // because third-party storage is blocked.
    const stripeTab = window.open("", "_blank");
    try {
      if (stripeTab && stripeTab.document) {
        stripeTab.document.title = "Redirecting to secure checkout…";
        stripeTab.document.body.style.cssText = "margin:0;font-family:system-ui,-apple-system,sans-serif;background:#FAF6EF;color:#231031;display:flex;align-items:center;justify-content:center;height:100vh;";
        stripeTab.document.body.innerHTML = '<div style="text-align:center"><div style="font:400 18px/1.4 Georgia,serif;color:#3B1A4D">Taking you to secure checkout…</div><div style="margin-top:10px;font-size:13px;color:#6E5A6E">Please keep this tab open.</div></div>';
      }
    } catch (_) { /* cross-origin or null — ignore */ }

    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        email,
        origin_url: window.location.origin,
      });
      const url = res.data?.checkout_url;
      if (!url) {
        if (stripeTab) { try { stripeTab.close(); } catch (_) {} }
        toast.error("Could not start checkout");
        return;
      }
      if (stripeTab && !stripeTab.closed) {
        // Preferred path: redirect the already-open new tab.
        stripeTab.location.href = url;
        toast.success("Opening secure checkout in a new tab…");
        return;
      }
      // Popup blocked — try top-level navigation, then self navigation.
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = url;
          return;
        }
      } catch (_) {
        // cross-origin top frame — fall through
      }
      window.location.assign(url);
    } catch (e) {
      if (stripeTab) { try { stripeTab.close(); } catch (_) {} }
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="checkout-page">
      <Header />
      <CartDrawer />
      <section className="max-w-5xl mx-auto px-5 lg:px-10 pt-10 sm:pt-12 pb-24 w-full grid lg:grid-cols-5 gap-10 lg:gap-12">
        <div className="lg:col-span-3">
          <div className="text-xs uppercase tracking-[0.25em] text-gold">Secure checkout</div>
          <h1 className="editorial font-serif-display text-4xl sm:text-5xl mt-3 text-plum-deep">Checkout</h1>
          <p className="mt-3 text-plum/70 max-w-md">We'll send your download links to this email right after purchase.</p>
          <div className="mt-10 space-y-6 max-w-md">
            <div>
              <Label htmlFor="co-email" className="text-xs uppercase tracking-widest text-plum/60">Email</Label>
              <Input
                id="co-email"
                type="email"
                placeholder="you@example.com"
                className="mt-2 h-12 bg-white border-brand"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="checkout-email-input"
              />
            </div>
            <button
              onClick={onPay}
              disabled={loading || items.length === 0}
              className="btn-primary w-full justify-center disabled:opacity-50"
              data-testid="pay-with-stripe-btn"
            >
              {loading ? "Redirecting to Stripe…" : `Pay ${formatPrice(subtotal)} with Stripe`}
            </button>
            <div className="flex items-center gap-4 text-[11px] text-plum/60">
              <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-gold" /> Secure</span>
              <span className="inline-flex items-center gap-1"><Download className="h-3.5 w-3.5 text-gold" /> Instant delivery</span>
            </div>
            <div className="text-[11px] text-plum/50">Test card: 4242 4242 4242 4242 · any future expiry · any CVC.</div>
          </div>
        </div>
        <aside className="lg:col-span-2">
          <div className="bg-white border border-brand rounded-sm p-6">
            <div className="font-serif-display text-xl text-plum-deep mb-4">Order summary</div>
            {items.length === 0 ? (
              <div className="text-sm text-plum/60">Your cart is empty. <Link to="/shop" className="underline text-plum-deep">Browse products</Link>.</div>
            ) : (
              <ul className="space-y-4">
                {items.map((i) => (
                  <li key={i.product_id} className="flex gap-4" data-testid={`co-item-${i.product_id}`}>
                    <div className="w-14 aspect-[4/5] product-media">
                      {i.image_url ? <img src={resolveImage(i.image_url)} alt="" /> : <div className="h-full w-full ph-checker" />}
                    </div>
                    <div className="flex-1 text-sm">
                      <div className="font-medium text-plum-deep">{i.title}</div>
                      <div className="text-plum/60">× {i.quantity}</div>
                    </div>
                    <div className="text-sm text-plum-deep">{formatPrice(i.price * i.quantity)}</div>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-6 border-t border-brand pt-4 flex items-center justify-between">
              <span className="text-sm text-plum/70">Total</span>
              <span className="font-serif-display text-xl text-plum-deep" data-testid="checkout-total">{formatPrice(subtotal)}</span>
            </div>
          </div>
        </aside>
      </section>
      <Footer />
    </div>
  );
}
